import { time } from "./time.js";

/**
 * Enhanced logging system that automatically includes context information
 * about components, namespaces, and UIDs.
 *
 * Supports forwarding logs to a dev server via WebSocket for unified
 * terminal output (development only).
 */
// No-op function for disabled logging - single shared instance
const noop = () => {};

class Logs {
  #isActive = false;
  #filterPatterns = new Set();  // Store filter patterns for quick lookup
  #isLoggingAll = false;        // Quick check flag for "all"
  #isLoggingNone = false;       // Quick check flag for "none"
  #ws = null;                   // WebSocket connection for remote logging
  #wsQueue = [];                // Queue for messages before WS connects
  #wsEnabled = false;           // Whether remote logging is enabled
  #registry = new Map();        // namespace -> { component, logFn, debugFn }
  
  /**
   * Activate logging
   */
  activateLogs() {
    this.#isActive = true;
  }

  /**
   * Enable remote logging to dev server via WebSocket.
   * Connects to the dev server's log endpoint.
   * @param {string} [path="/__frontend_logs"] - WebSocket path
   */
  enableRemoteLogging(path = "/__frontend_logs") {
    if (typeof WebSocket === "undefined") {
      return; // Not in browser environment
    }

    this.#wsEnabled = true;
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    this.#ws = new WebSocket(`${protocol}//${location.host}${path}`);

    this.#ws.addEventListener("open", () => {
      while (this.#wsQueue.length > 0) {
        this.#ws.send(this.#wsQueue.shift());
      }
    });

    this.#ws.addEventListener("error", () => {
      // Silently ignore - remote logging is optional
      this.#wsEnabled = false;
    });
  }

  /**
   * Send a structured log message to the dev server
   * @param {string} level - Log level (log, warn, error)
   * @param {string} namespace - Component namespace
   * @param {string|null} uid - Entity UID
   * @param {string} message - Log message
   * @param {Array} data - Additional data
   * @private
   */
  #sendRemote(level, namespace, uid, message, data) {
    if (!this.#wsEnabled) {
      return;
    }

    const payload = JSON.stringify({
      level,
      namespace,
      uid,
      message,
      data: data.length > 0 ? data : undefined,
      timestamp: Math.floor(time.current),
    });

    if (this.#ws && this.#ws.readyState === WebSocket.OPEN) {
      this.#ws.send(payload);
    } else {
      this.#wsQueue.push(payload);
    }
  }
  
  /**
   * Register a component for log method swapping.
   * Called by createSingletonLogger and createEntityLogger.
   * @param {string} namespace - Component namespace
   * @param {Object} component - The component instance
   * @param {Function} logFn - The real log function for this component
   */
  register(namespace, component, logFn) {
    this.#registry.set(namespace, { component, logFn });
    // Apply current filter state to this component
    this.#applyFilterToComponent(namespace, component, logFn);
  }

  /**
   * Apply current filter state to a single component
   * @private
   */
  #applyFilterToComponent(namespace, component, logFn) {
    const shouldLog = this.#shouldLogNamespace(namespace);
    component.log = shouldLog ? logFn : noop;
    // debug is NOT swapped - always works when active
  }

  /**
   * Check if a namespace should have logging enabled
   * @param {string} namespace - Component namespace
   * @returns {boolean}
   * @private
   */
  #shouldLogNamespace(namespace) {
    if (!this.#isActive) return false;
    if (this.#isLoggingNone) return false;
    if (this.#isLoggingAll) return true;

    // Check exact match
    if (this.#filterPatterns.has(namespace)) return true;

    // Check prefix match (e.g., "visual" matches "visual:image")
    const namespaceParts = namespace.split(':');
    for (let i = 1; i < namespaceParts.length; i++) {
      const partialNamespace = namespaceParts.slice(0, i).join(':');
      if (this.#filterPatterns.has(partialNamespace)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Initialize log filters from a comma-separated string
   * @param {string} filterString - Comma-separated list of log filters
   *                              - "all" logs everything
   *                              - "none" logs nothing
   *                              - Namespace filters like "visual:image", "core-engine"
   *                              - Entity UID filters
   */
  initializeFilters(filterString) {
    this.#filterPatterns.clear();
    this.#isLoggingAll = false;
    this.#isLoggingNone = false;

    if (!filterString || filterString.trim() === "" || filterString.includes("none")) {
      this.#isLoggingNone = true;
    } else {
      const patterns = filterString.split(',').map(p => p.trim());

      if (patterns.includes("all")) {
        this.#isLoggingAll = true;
      } else {
        // Store patterns for later matching
        for (const pattern of patterns) {
          this.#filterPatterns.add(pattern);
        }
      }
    }

    // Apply filters to all registered components
    for (const [namespace, { component, logFn }] of this.#registry) {
      this.#applyFilterToComponent(namespace, component, logFn);
    }
  }
  
  /**
   * Check if a log entry should be output based on filters
   * @param {string} namespace - Component namespace
   * @param {string} [uid] - Entity UID (optional)
   * @param {string} [logLevel] - Log level (log, warn, error)
   * @returns {boolean} - True if log should be output
   * @private
   */
  #shouldLog(namespace, uid, logLevel) {
    // Early rejection if logging is disabled
    if (!this.#isActive) {
      return false;
    }
    
    // Always show warn and error logs even if logging is set to none
    if (logLevel === 'warn' || logLevel === 'error') {
      return true;
    }
    
    // For regular logs, check if logging none is set
    if (this.#isLoggingNone) {
      return false;
    }
    
    // Early acceptance if logging all
    if (this.#isLoggingAll) {
      return true;
    }
    
    // Check namespace matches (full or prefix matching)
    if (this.#filterPatterns.has(namespace)) {
      return true;
    }
    
    // Check for partial namespace matches (e.g., "visual" matches "visual:image")
    const namespaceParts = namespace.split(':');
    for (let i = 1; i < namespaceParts.length; i++) {
      const partialNamespace = namespaceParts.slice(0, i).join(':');
      if (this.#filterPatterns.has(partialNamespace)) {
        return true;
      }
    }
    
    // Check for entity UID matches (if applicable)
    if (uid && this.#filterPatterns.has(uid)) {
      return true;
    }
    
    return false;
  }

  /**
   * Format a value for logging, handling objects and other types
   * @param {any} value - The value to format
   * @returns {string} The formatted string value
   */
  #formatValue(value) {
    if (typeof value === 'object' && value !== null) {
      try {
        return JSON.stringify(value, null, 2);
      } catch (e) {
        return '[Complex Object]';
      }
    }
    return String(value);
  }

  /**
   * Extract the first relevant stack frame (skipping framework internals)
   * @param {string[]} stackLines - Array of stack trace lines
   * @returns {string|null} The first relevant frame or null
   * @private
   */
  #getRelevantFrame(stackLines) {
    // Skip first line ("Error") and framework internals
    const frameworkPatterns = [
      '/dmafjs-core/',
      '/dmafjs-visual/',
      '/dmafjs-runtime/',
      'logs.js',
      'singleton-logger.js'
    ];

    for (let i = 1; i < stackLines.length; i++) {
      const line = stackLines[i].trim();
      const isFramework = frameworkPatterns.some(pattern => line.includes(pattern));
      if (!isFramework && line.startsWith('at ')) {
        // Clean up the frame: "at Function (file:line:col)" -> "at Function (file:line)"
        return line.replace(/:\d+\)$/, ')');
      }
    }

    // Fallback to first "at" line if no external frame found
    for (let i = 1; i < stackLines.length; i++) {
      const line = stackLines[i].trim();
      if (line.startsWith('at ')) {
        return line.replace(/:\d+\)$/, ')');
      }
    }

    return null;
  }

  /**
   * Log a message
   * @param {string} namespace - The component namespace
   * @param {string|null} uid - Component UID or null
   * @param {string} message - The log message
   * @param {...any} data - Additional data to log
   */
  log(namespace, uid, message, ...data) {
    // Fast early return if logging is definitely disabled
    if (!this.#isActive) {
      return;
    }

    // Check if this specific log should be output based on filters
    if (!this.#shouldLog(namespace, uid, 'log')) {
      return;
    }

    this.#sendRemote('log', namespace, uid, message, data);

    const timestamp = Math.floor(time.current);
    const formattedData = data.map(item => this.#formatValue(item));

    if (uid) {
      console.log(`[${timestamp}] [${namespace}] [${uid}] ${message}`, ...formattedData);
    } else {
      console.log(`[${timestamp}] [${namespace}] ${message}`, ...formattedData);
    }
  }

  /**
   * Log a warning
   * @param {string} namespace - The component namespace
   * @param {string|null} uid - Component UID or null
   * @param {string} message - The log message
   * @param {...any} data - Additional data to log
   */
  warn(namespace, uid, message, ...data) {
    // Fast early return if logging is definitely disabled
    if (!this.#isActive) {
      return;
    }

    // Check if this specific log should be output based on filters
    if (!this.#shouldLog(namespace, uid, 'warn')) {
      return;
    }

    // Get one relevant stack frame
    const stackLines = new Error().stack.split('\n');
    const frame = this.#getRelevantFrame(stackLines);

    this.#sendRemote('warn', namespace, uid, message, [...data, { frame }]);

    const timestamp = Math.floor(time.current);
    const formattedData = data.map(item => this.#formatValue(item));
    const frameStr = frame ? ` ${frame}` : '';

    if (uid) {
      console.warn(`[${timestamp}] [${namespace}] [${uid}] ${message}${frameStr}`, ...formattedData);
    } else {
      console.warn(`[${timestamp}] [${namespace}] ${message}${frameStr}`, ...formattedData);
    }
  }

  /**
   * Log an error
   * @param {string} namespace - The component namespace
   * @param {string|null} uid - Component UID or null
   * @param {string} message - The log message
   * @param {...any} data - Additional data to log
   */
  error(namespace, uid, message, ...data) {
    // Fast early return if logging is definitely disabled
    if (!this.#isActive) {
      return;
    }

    // Check if this specific log should be output based on filters
    if (!this.#shouldLog(namespace, uid, 'error')) {
      return;
    }

    // Get one relevant stack frame
    const stackLines = new Error().stack.split('\n');
    const frame = this.#getRelevantFrame(stackLines);

    this.#sendRemote('error', namespace, uid, message, [...data, { frame }]);

    const timestamp = Math.floor(time.current);
    const formattedData = data.map(item => this.#formatValue(item));
    const frameStr = frame ? ` ${frame}` : '';

    if (uid) {
      console.error(`[${timestamp}] [${namespace}] [${uid}] ${message}${frameStr}`, ...formattedData);
    } else {
      console.error(`[${timestamp}] [${namespace}] ${message}${frameStr}`, ...formattedData);
    }
  }

  /**
   * Log a critical error (throws)
   * @param {string} namespace - The component namespace
   * @param {string|null} uid - Component UID or null
   * @param {string} message - The log message
   * @param {...any} data - Additional data to log
   */
  critical(namespace, uid, message, ...data) {
    // Critical errors are always logged regardless of filters
    // but still respect the overall activation state
    if (!this.#isActive) {
      return;
    }
    
    const timestamp = Math.floor(time.current);
    const formattedData = data.map(item => this.#formatValue(item));
    
    let errorMessage;
    if (uid) {
      errorMessage = `[${timestamp}] [${namespace}] [${uid}] ${message} ${formattedData.join(' ')}`;
    } else {
      errorMessage = `[${timestamp}] [${namespace}] ${message} ${formattedData.join(' ')}`;
    }
    
    throw new Error(errorMessage);
  }

  /**
   * Debug log - temporary debugging. Easy to grep and remove before commit.
   * Always logs when active (no filtering).
   * @param {string} namespace - The component namespace
   * @param {string|null} uid - Component UID or null
   * @param {string} message - The log message
   * @param {...any} data - Additional data to log
   */
  debug(namespace, uid, message, ...data) {
    if (!this.#isActive) {
      return;
    }

    this.#sendRemote('debug', namespace, uid, message, data);

    const timestamp = Math.floor(time.current);
    const formattedData = data.map(item => this.#formatValue(item));

    if (uid) {
      console.log(`[${timestamp}] [${namespace}] [${uid}] ${message}`, ...formattedData);
    } else {
      console.log(`[${timestamp}] [${namespace}] ${message}`, ...formattedData);
    }
  }

  /**
   * Legacy support method for old addLogPath function
   * @param {string} logPath - Path to log
   */
  addLogPath(logPath) {
    // This is maintained for backward compatibility
    // It adds the logPath to the filter patterns
    if (logPath) {
      this.#filterPatterns.add(logPath);
    }
  }
}

const logs = new Logs();
export { Logs, logs };